import { useCallback } from 'react';
import { fetchMyQuoteDashboard } from '@shared/api/patient-quotes';
import { stripDoctorControlPath } from '@shared/lib/doctor-workspace';
import { cacheUtils, queryKeys } from '@shared/lib/react-query';
import { fetchDoctorQuotes, fetchEligibleRequests } from '@features/doctors/quotes/api';
import { quotingQueueApi } from '@features/quoting-queue/api';
import { travelPlanningApi } from '@features/quoting-queue/pages/travel-planning/api';
import { useDoctorWorkspace } from '@shared/hooks/useDoctorWorkspace';

type AppRole = 'patient' | 'doctor' | 'admin' | 'super_admin' | 'org_owner';

type PrefetchInput = {
  path: string;
  role: AppRole;
};

const prefetchRouteChunk = (path: string, role: AppRole) => {
  const normalizedPath = stripDoctorControlPath(path) ?? path;

  if (normalizedPath === '/quoting-queue' || normalizedPath === '/super-admin/kanban') {
    void import('@features/admin/pages/quoting-queue');
    return;
  }

  if (normalizedPath === '/quotes') {
    if (role === 'doctor' || role === 'admin') {
      void import('@features/doctors/quotes');
      return;
    }

    void import('@features/patient/pages/quotes');
  }
};

export const useNavPrefetch = () => {
  const { activeDoctorControlRequest, effectiveDoctorId, isDelegatedDoctorWorkspace } =
    useDoctorWorkspace();
  const doctorQuotesScope = isDelegatedDoctorWorkspace
    ? (activeDoctorControlRequest?.id ?? effectiveDoctorId ?? 'delegated')
    : (effectiveDoctorId ?? 'native');

  const prefetchPath = useCallback(
    async ({ path, role }: PrefetchInput) => {
      const normalizedPath = stripDoctorControlPath(path) ?? path;
      prefetchRouteChunk(path, role);

      if (normalizedPath === '/quoting-queue' || normalizedPath === '/super-admin/kanban') {
        await cacheUtils.prefetchQuery(
          queryKeys.quotingQueue.list({ limit: 200 }),
          () =>
            quotingQueueApi.list(
              { limit: 200 },
              {
                timeoutProfile: 'quotingQueue',
                requestLabel: 'prefetch:quoting-queue',
              }
            ),
          { staleTime: 30 * 1000 }
        );
        return;
      }

      if (normalizedPath === '/quotes' && role === 'patient') {
        await cacheUtils.prefetchQuery(queryKeys.patientQuotes.dashboard(), fetchMyQuoteDashboard, {
          staleTime: 60 * 1000,
        });
        return;
      }

      if (normalizedPath === '/quotes' && role === 'doctor') {
        await Promise.allSettled([
          cacheUtils.prefetchQuery(
            queryKeys.eligibleRequests.list({
              scope: doctorQuotesScope,
              page: 1,
              limit: 20,
              search: '',
            }),
            () =>
              fetchEligibleRequests(
                { page: 1, limit: 20 },
                {
                  timeoutProfile: 'dashboard',
                  requestLabel: 'prefetch:doctor-eligible-requests',
                }
              ),
            { staleTime: 30 * 1000 }
          ),
          cacheUtils.prefetchQuery(
            queryKeys.doctorQuotes.list({ scope: doctorQuotesScope, page: 1, limit: 20 }),
            () =>
              fetchDoctorQuotes(
                { page: 1, limit: 20 },
                {
                  timeoutProfile: 'dashboard',
                  requestLabel: 'prefetch:doctor-quotes-list',
                }
              ),
            { staleTime: 30 * 1000 }
          ),
        ]);
      }
    },
    [doctorQuotesScope]
  );

  const prefetchTravelPlanning = useCallback(async (travelCaseId: string) => {
    if (!travelCaseId) return;

    void import('@features/quoting-queue/pages/travel-planning/TravelPlanningPage');

    await cacheUtils.prefetchQuery(
      queryKeys.travelPlanning.detail(travelCaseId),
      () =>
        travelPlanningApi.load(travelCaseId, {
          timeoutProfile: 'travelPlanning',
          requestLabel: 'prefetch:travel-planning-detail',
        }),
      { staleTime: 60 * 1000 }
    );
  }, []);

  return {
    prefetchPath,
    prefetchTravelPlanning,
  };
};
